/**
 * Analytics — loads GA4 + Meta Pixel only when their IDs are set in env vars.
 * Exposes a single track() helper so call sites don't care which platforms are active.
 *
 * Env vars (set in Emergent Deployment Panel → Secrets):
 *   REACT_APP_GA_ID    = G-XXXXXXXXXX  (Google Analytics 4 Measurement ID)
 *   REACT_APP_FB_PIXEL = 1234567890123 (Meta/Facebook Pixel ID)
 */

const GA_ID = process.env.REACT_APP_GA_ID;
const FB_PIXEL = process.env.REACT_APP_FB_PIXEL;

let initialized = false;

export function initAnalytics() {
    if (initialized || typeof window === "undefined") return;
    initialized = true;

    if (GA_ID) {
        // gtag.js loader
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
        document.head.appendChild(s);

        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
        window.gtag("config", GA_ID, { send_page_view: true });
    }

    if (FB_PIXEL) {
        // Meta Pixel snippet (standard)
        /* eslint-disable */
        !(function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        /* eslint-enable */
        window.fbq("init", FB_PIXEL);
        window.fbq("track", "PageView");
    }
}

/** Track an event across both platforms. Silent no-op when neither is configured. */
export function track(eventName, params = {}) {
    if (typeof window === "undefined") return;

    // Google Analytics 4
    if (GA_ID && typeof window.gtag === "function") {
        window.gtag("event", eventName, params);
    }

    // Meta Pixel — map common events to their standard names
    if (FB_PIXEL && typeof window.fbq === "function") {
        const fbMap = {
            view_item: "ViewContent",
            add_to_cart: "AddToCart",
            begin_checkout: "InitiateCheckout",
            purchase: "Purchase",
            sign_up: "CompleteRegistration",
            lead: "Lead",
        };
        const fbEvent = fbMap[eventName];
        if (fbEvent) {
            window.fbq("track", fbEvent, params);
        } else {
            window.fbq("trackCustom", eventName, params);
        }
    }
}


/* ---------- First-party funnel beacon (independent of GA / Pixel) ---------- */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FUNNEL_KEY = "fgm_sid";

function getSessionId() {
    try {
        let sid = localStorage.getItem(FUNNEL_KEY);
        if (!sid) {
            sid =
                (crypto.randomUUID && crypto.randomUUID()) ||
                `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(FUNNEL_KEY, sid);
        }
        return sid;
    } catch {
        return "anon";
    }
}

/** Fire-and-forget beacon. Backend dedupes page_view per session within 30 min.
 *  Also captures UTM params from URL on first call per session (sticky in localStorage).
 */
function getStickyUtm() {
    try {
        const KEY = "fgm_utm";
        const stored = JSON.parse(localStorage.getItem(KEY) || "null");
        const params = new URLSearchParams(window.location.search);
        const fromUrl = {};
        for (const k of ["source", "medium", "campaign", "content"]) {
            const v = params.get(`utm_${k}`);
            if (v) fromUrl[k] = v;
        }
        if (Object.keys(fromUrl).length > 0) {
            localStorage.setItem(KEY, JSON.stringify(fromUrl));
            return fromUrl;
        }
        return stored || {};
    } catch {
        return {};
    }
}

export function trackFunnel(name) {
    if (typeof window === "undefined") return;
    const body = JSON.stringify({
        name,
        session_id: getSessionId(),
        utm: getStickyUtm(),
    });
    try {
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(`${API}/funnel/event`, blob);
            return;
        }
    } catch {
        // fall through to fetch
    }
    fetch(`${API}/funnel/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
    }).catch(() => {});
}
